<template>
  <div class="verify-certificate-page">
    <HeroSection
      :title="$c.text('pages.verify-certificate.title')"
      :subtitle="$c.text('pages.verify-certificate.subtitle')"
      :image-url="imageUrl"
      class-name="verify-certificate-hero left-shadow right-shadow"
    />

    <div class="container">
      <div class="site-section row">
        <div class="col col-7">
          <h2 class="h3">
            {{ $config.platformName }} Certificates
          </h2>
          <p class="body-1">
            {{ $c.text('pages.verify-certificate.caption') }}
          </p>
        </div>
        <div class="tabs">
          <div class="col col-3">
            <div class="tab" :class="{ active: tab === 'link' }" @click="changeTab('link')">
              <IconLink class="icon-link" />
              <span class="body-2">Credential link</span>
            </div>
            <div class="tab" :class="{ active: tab === 'code' }" @click="changeTab('code')">
              <IconCode class="icon-code" />
              <span class="body-2">Credential code</span>
            </div>
          </div>
          <div v-if="tab === 'link'" class="col col-9">
            <div class="credential-body">
              <p class="h4">
                Enter A Credential Link
              </p>
              <input v-model="linkInput" type="text" :placeholder="`${$config.lmsUrl}/c/58cc92`">
              <button class="btn-primary" @click="verify">
                Verify
              </button>
            </div>
          </div>
          <div v-else class="col col-9">
            <div class="credential-body">
              <p class="h4">
                Enter A Credential Code
              </p>
              <input v-model="codeInput" type="text" placeholder="BB-AC4B381720">
              <button class="btn-primary" @click="verify">
                Verify
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import global from '@itbpbg/lms-components/src/mixins/global';
import HeroSection from '@itbpbg/lms-components/src/components/HeroSection.vue';
import IconLink from '../assets/svg/icon-link.svg?vue-component';
import IconCode from '../assets/svg/icon-code.svg?vue-component';

export default {
  components: {
    HeroSection,
    IconLink,
    IconCode,
  },
  mixins: [global],
  async asyncData({ $axios, route, $config }) {
    const page = await $axios.$get(`/pages/${route.fullPath}`).catch(() => false);
    page.fullPath = `${$config.siteUrl}${route.fullPath}`;
    page.metaFullImageUrl = `${$config.siteUrl}/resources/${page.metaImage}`;

    page.created = page.created || new Date();
    page.updated = page.updated || new Date();

    return {
      page,
    };
  },
  data() {
    return {
      tab: 'link',
      codeInput: '',
      linkInput: '',
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps(this.page),
      script: [linkedData],
    };
  },
  computed: {
    imageUrl() {
      if (this.$config.platform === 'fa') {
        if (this.$store.state.layout === 'desktop' || this.$store.state.tablet) {
          return 'fa-verify-certificate-desktop.jpg';
        }
        return 'fa-verify-certificate-mobile.jpg';
      } if (this.$config.platform === 'ds' && (this.$store.state.layout === 'desktop' || this.$store.state.tablet)) {
        return 'verify-certificate.jpg';
      }
      return 'verify-certificate-mobile.jpg';
    },
  },
  methods: {
    changeTab(tab) {
      this.tab = tab;
    },
    async verify() {
      if (this.tab === 'link' && (this.linkInput.indexOf('/c/') > -1 || this.linkInput.indexOf('/certificates/') > -1)) {
        if (this.linkInput.charAt(this.linkInput.length - 1) === '/') {
          this.linkInput = this.linkInput.substr(0, this.linkInput.length - 1);
        }
        const [url] = this.linkInput.split('/').reverse();
        const { certificate } = await this.$axios.$get(`/certificates/${url}`).catch(() => ({}));
        if (certificate) {
          return this.$store.dispatch('modals/open', { name: 'verified-certificate', certificate, avatar: certificate.ownerImage });
        }
      } else if (this.tab === 'code' && this.codeInput) {
        const { certificate } = await this.$axios.$get(`/certificates/${this.codeInput}`).catch(() => ({}));
        if (certificate) {
          return this.$store.dispatch('modals/open', { name: 'verified-certificate', certificate, avatar: certificate.ownerImage });
        }
      }
      return this.$store.dispatch('modals/open', { name: 'unverified-certificate' });
    },
  },
};
</script>

<style lang="scss" scoped>
.verify-certificate-page {
  .verify-certificate-hero {
    overflow: hidden;

    ::v-deep {
      .container .row > div .subtitle {
        font-weight: 400;
      }

      .wall {
        &:before {
          left: -250px;
          width: 60%;
          background: linear-gradient(90deg, var(--main-dark) 0%, var(--main-dark) 50%, rgba(1, 22, 51, 0) 100%);
        }

        &:after {
          right: -570px;
        }
      }

      .container:after {
        content: none;
      }
    }
  }
}

.site-section {
  padding: var(--spacing-80) 0;

  .body-1 {
    margin: 0;
  }
}

.h3 {
  margin-bottom: var(--spacing-36);
  font-size: 32px;
}

.tabs {
  width: 100%;
  margin-top: 60px;
}

.tab,
.credential-body {
  display: flex;
  flex-direction: column;
}

.tab {
  height: 116px;
  align-items: center;
  justify-content: center;
  color: var(--main-dark-light-3);
  filter: drop-shadow(0 3px 8px rgba(0, 12, 31, 0.05));
  border-radius: 10px;
  cursor: pointer;

  svg {
    border-radius: 4px;
  }

  &:hover {
    border: 1px solid var(--accent);
  }

  &.active {
    border: 1px solid var(--accent);
    color: var(--accent);

    .icon-link path {
      stroke: var(--accent);
    }

    .icon-code g {
      fill: var(--accent);
    }
  }

  &:first-of-type {
    margin-bottom: var(--spacing-8);
  }

  span {
    margin-top: var(--spacing-4);
  }
}

.credential-body {
  padding: var(--spacing-32) var(--spacing-lg) 37px;
  background-color: var(--main-dark-light-1);
  border-radius: 10px;
  color: var(--main-light);

  .h4 {
    margin-bottom: var(--spacing-32);
  }

  input {
    margin-bottom: var(--spacing-16);
    max-width: 536px;
    background-color: transparent;
    border: 1px solid var(--main-dark-light-4);
    color: var(--main-light);
    border-radius: 5px;

    &::placeholder {
      color: var(--main-light-shade-4);
    }

    &:focus {
      border: 1px solid var(--main-light-shade-4);

      &::placeholder {
        color: transparent;
      }
    }
  }

  .btn-primary {
    max-width: 200px;
    height: 48px;
    font-weight: 600;
  }
}

a.btn-primary {
  height: 48px;
  width: 272px;
  font-weight: 600;
  margin-top: var(--spacing-36);
  display: flex;
  justify-content: center;
  align-items: center;
}

@include bp($bp-mobile) {
  .verify-certificate-page {
    position: relative;

    .verify-certificate-hero {
      ::v-deep {
        .wall:after {
          content: unset;
        }

        .container {
          .heading .h2 {
            font-size: 32px;
            margin-bottom: var(--spacing-48);
          }

          &:after {
            content: '';
          }
        }
      }
    }
  }

  .container {
    padding-left: 0;
    padding-right: 0;
  }

  .row {
    margin-left: 0;
    margin-right: 0;
  }

  .col {
    padding-left: var(--spacing-24);
    padding-right: var(--spacing-24);
  }

  .col-7 {
    h3 {
      font-size: 32px;
    }

    .btn-primary {
      height: 52px;
      width: 100%;
      max-width: 327px;
      padding: 0;
    }
  }

  .col-3 {
    display: flex;
    justify-content: space-between;

    div {
      width: 100%;
    }
  }

  .tabs {
    background-color: var(--main-dark-light-1);
    padding-top: 54px;
    padding-bottom: var(--spacing-60);
    margin-bottom: var(--spacing-80);

    .credential-body {
      height: auto;
      padding-left: 0;
      padding-right: 0;
      padding-bottom: 0;

      .h4 {
        font-size: 24px;
      }

      .btn-primary {
        height: 52px;
        width: 100%;
        max-width: 327px;
        padding: 0;
      }
    }
  }

  .teachable {
    .body-1 {
      font-size: 18px;
    }
  }
}

@include bp($bp-phone) {
  .verify-certificate-page {
    ::v-deep {
      .hero-section-wrapper {
        .container.image-container {
          padding-bottom: 112px;
        }

        .wall img {
          object-fit: contain;
          margin-top: var(--spacing-40);
        }
      }
    }

    &:after {
      content: '';
      position: absolute;
      top: -23px;
      left: 0;
      width: 100%;
      height: 214px;
      background: linear-gradient(180deg, var(--main-dark) 0%, var(--main-dark) 0.01%, rgba(0, 12, 31, 0.98) 56.77%, rgba(0, 12, 31, 0) 100%);
    }
  }

  .tabs .credential-body .btn-primary {
    max-width: 100%;
  }
}

.fa {
  @import '../assets/sass/fa/pages/verify-certificate.scss';
}
</style>
